import { createStore, ObservableMap } from "@stencil/store";
import { getRegisteredGlobal, registerGlobal } from "@trumpf/ux.utils";
import { debounce } from "../../../utils/debounce";
import {
    FilterApiError,
    FilterBlockTypesEnum,
    FilterData,
    FilterDataProvider,
    FilterDataProviderCallback,
    FilterDataProviderState,
    FilterItem,
    FilterOption,
} from "./interface";
import { requestFilter } from "../api";
import { NS_FILTER_PROVIDER } from "../constants";

class FilterDataProviderInstance implements FilterDataProvider {
    private dataPromise: Promise<FilterData | null> | null = null;
    private readonly scope: string;
    private readonly store: ObservableMap<FilterDataProviderState>;
    private readonly subscribers: FilterDataProviderCallback[] = [];

    constructor(scope: string) {
        this.scope = scope;

        // Initialize local store
        this.store = createStore<FilterDataProviderState>({
            data: null,
            endpoint: null,
            error: null,
            pending: false,
            filterData: null,
            filterQuery: "",
            filterQuerySet: false,
        });

        // Write session data to store
        const sessionData = this.readSessionStorage();
        if (sessionData.filterQuery) {
            this.store.set("filterQuery", sessionData.filterQuery);
            this.store.set("filterQuerySet", true);
        }

        // Fetch data if endpoint or query have changed
        this.store.onChange("endpoint", this.fetchDataDebounced);
        this.store.onChange("filterQuery", this.fetchDataDebounced);

        // Notify subscribers if state has changed
        this.store.onChange("data", this.notifySubscribersDebounced);
        this.store.onChange("error", this.notifySubscribersDebounced);
        this.store.onChange("filterData", this.notifySubscribersDebounced);
        this.store.onChange("pending", this.notifySubscribersDebounced);
    }

    get endpoint(): string {
        return this.state.endpoint;
    }

    get data(): FilterData | null {
        return this.state.data;
    }

    get error(): FilterApiError | null {
        return this.state.error;
    }

    get pending(): boolean {
        return this.state.pending;
    }

    get filterData(): FilterData | null {
        return this.state.filterData;
    }

    get filterQuery(): string {
        return this.state.filterQuery;
    }

    get filterQuerySet() {
        return this.state.filterQuerySet;
    }

    get state() {
        return this.store.state;
    }

    private getNamespace = () => `${NS_FILTER_PROVIDER}.${this.scope}`;

    private readSessionStorage = () => {
        const jsonStr = window.sessionStorage.getItem(this.getNamespace());
        return typeof jsonStr === "string" ? JSON.parse(jsonStr) : {};
    };

    private updateSessionStorage = () => {
        const data = {
            ...this.readSessionStorage(),
            filterQuery: this.filterQuery,
        };
        window.sessionStorage.setItem(
            this.getNamespace(),
            JSON.stringify(data)
        );
    };

    setEndpoint(value: string) {
        this.store.set("endpoint", value || null);
    }

    setFilterQuery(filterQuery: string) {
        this.store.set("filterQuery", filterQuery);
        this.store.set("filterQuerySet", true);
        this.updateFilterData();
        this.updateSessionStorage();
    }

    private updateFilterData = () => {
        const { data, filterQuery } = this;
        if (data === null || !Array.isArray(data.filterBlocks)) {
            this.store.set("filterData", null);
            return;
        }

        const params = new URLSearchParams(filterQuery);

        const isActiveFilterItem = (item: FilterItem): boolean => {
            if (item.disabled) {
                return item.active;
            }
            const queryValues = params.getAll(item.name);
            if (queryValues.length === 0) {
                return false;
            }
            return queryValues.includes(item.value);
        };

        const isSelectedFilterOption = (
            name: string,
            option: FilterOption
        ): boolean => {
            if (option.disabled) {
                return option.selected;
            }
            const queryValues = params.getAll(name);
            if (queryValues.length === 0) {
                return false;
            }
            return queryValues.includes(option.value);
        };

        const filterData: FilterData = {
            ...data,
            filterBlocks: data.filterBlocks.map((filterBlock) => {
                switch (filterBlock.type) {
                    case FilterBlockTypesEnum.Select:
                        return {
                            ...filterBlock,
                            options: filterBlock.options.map(
                                (filterOption) => ({
                                    ...filterOption,
                                    selected: isSelectedFilterOption(
                                        filterBlock.multiple
                                            ? `${filterBlock.name}[]`
                                            : filterBlock.name,
                                        filterOption
                                    ),
                                })
                            ),
                        };
                    case FilterBlockTypesEnum.Checkbox:
                    case FilterBlockTypesEnum.Dropdown:
                        return {
                            ...filterBlock,
                            filterItems: filterBlock.filterItems.map(
                                (filterItem) => ({
                                    ...filterItem,
                                    active: isActiveFilterItem({
                                        ...filterItem,
                                        name: filterItem.name
                                            ? filterItem.name
                                            : `${filterBlock.name}[]`,
                                    }),
                                })
                            ),
                        };
                    default:
                        return filterBlock;
                }
            }),
        };
        this.checkIsFavFilter(data.filterBlocks, params);
        this.store.set("filterData", filterData);
    };

    getData(): Promise<FilterData | null> {
        if (this.dataPromise === null) {
            this.dataPromise = this.fetchData();
        }
        return this.dataPromise;
    }

    subscribe(handler: FilterDataProviderCallback): () => void {
        if (!this.subscribers.includes(handler)) {
            this.subscribers.push(handler);
        }
        return () => this.unsubscribe(handler);
    }

    unsubscribe(handler: FilterDataProviderCallback): void {
        const index = this.subscribers.indexOf(handler);
        if (index !== -1) {
            this.subscribers.splice(index, 1);
        }
    }

    private fetchData = async (): Promise<FilterData | null> => {
        if (this.endpoint === null) {
            return Promise.resolve(null);
        }
        this.store.set("pending", true);
        const response = await requestFilter(this.endpoint, this.filterQuery);
        let data: FilterData | null = null;
        if (response.ok) {
            data = await response.json();
            this.store.set("error", null);
            this.store.set("data", data);
        } else {
            this.store.set("data", null);
            this.store.set("error", {
                status: response.status,
                message: response.statusText,
            });
        }
        this.updateFilterData();
        this.updateSessionStorage();
        this.store.set("pending", false);
        this.dataPromise = null;
        return data;
    };

    private fetchDataDebounced = debounce(this.fetchData, 20);

    private notifySubscribers = () => {
        this.subscribers.forEach((handler) => {
            handler(this.state);
        });
    };

    private notifySubscribersDebounced = debounce(this.notifySubscribers, 20);

    private checkIsFavFilter = (
        filterBlocks: any[],
        params: URLSearchParams
    ) => {
        const filterName = "isFav";

        if (!Array.isArray(filterBlocks)) {
            return;
        }

        const hasIsFavFilter = filterBlocks.some(
            (filterBlock) =>
                Array.isArray(filterBlock.filterItems) &&
                filterBlock.filterItems.some(
                    (filterItem) => filterItem.name === filterName
                )
        );

        if (!hasIsFavFilter && params.has(filterName)) {
            params.delete(filterName);
            this.store.set("filterQuery", params.toString());
        }
    };
}

/**
 * Get the FilterDataProvider singleton for a specific scope
 * @param scope
 * @returns {FilterDataProvider}
 */
export function getFilterDataProvider(scope: string): FilterDataProvider {
    if (!/^[a-zA-Z0-9-_]+$/.test(scope)) {
        throw new Error("invalid scope");
    }
    const ns: string = `${NS_FILTER_PROVIDER}.${scope}`;
    let provider = getRegisteredGlobal<FilterDataProvider>(ns) || null;
    if (provider === null) {
        provider = new FilterDataProviderInstance(scope);
        registerGlobal(provider, ns);
    }
    return provider;
}
